import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";

import { CMSProvider, useCMS } from "@/hooks/useCMS";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SocialRail } from "@/components/SocialRail";

import Home from "@/pages/Home";
import Events from "@/pages/Events";
import Initiatives from "@/pages/Initiatives";
import About from "@/pages/About";
import BenniBooks from "@/pages/BenniBooks";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import ResourceManager from "@/pages/admin/ResourceManager";
import Submissions from "@/pages/admin/Submissions";
import SettingsPage from "@/pages/admin/SettingsPage";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import Rsvps from "@/pages/admin/Rsvps";
import EmailSettings from "@/pages/admin/EmailSettings";
import EmailTemplates from "@/pages/admin/EmailTemplates";
import Account from "@/pages/admin/Account";
import SectionsManager from "@/pages/admin/SectionsManager";
import SiteImages from "@/pages/admin/SiteImages";

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const tryScroll = (n) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        else if (n < 10) setTimeout(() => tryScroll(n + 1), 120);
      };
      tryScroll(0);
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [pathname, hash]);
  return null;
}

const DENSITY = { compact: "14px", cozy: "15px", comfortable: "16px", spacious: "18px" };

function AppearanceManager() {
  const { data } = useCMS();
  const density = data.settings?.density || "comfortable";
  useEffect(() => {
    document.documentElement.style.fontSize = DENSITY[density] || DENSITY.comfortable;
    return () => { document.documentElement.style.fontSize = ""; };
  }, [density]);
  return null;
}

function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <AppearanceManager />
      <SiteHeader />
      <SocialRail />
      <main className="flex-1">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <CMSProvider>
            <ScrollManager />
            <Toaster position="top-center" richColors />
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/Home" element={<Home />} />
                <Route path="/Events" element={<Events />} />
                <Route path="/Initiatives" element={<Initiatives />} />
                <Route path="/About" element={<About />} />
                <Route path="/BenniBooks" element={<BenniBooks />} />
                <Route path="/Blog" element={<Blog />} />
                <Route path="/Blog/:slug" element={<BlogArticle />} />
              </Route>

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="submissions" element={<Submissions />} />
                <Route path="rsvps" element={<Rsvps />} />
                <Route path="media" element={<MediaLibrary />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="email" element={<EmailSettings />} />
                <Route path="email-templates" element={<EmailTemplates />} />
                <Route path="account" element={<Account />} />
                <Route path="sections" element={<SectionsManager />} />
                <Route path="site-images" element={<SiteImages />} />
                <Route path=":collection" element={<ResourceManager />} />
              </Route>
            </Routes>
          </CMSProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
